if (!customElements.get('act-button')) {
  class ActButton extends HTMLElement {
    constructor() {
      super();
      this.productId = this.getAttribute('data-product-id');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.actModal = document.querySelector('act-modal');
      this.parsedProductJson = this.parseProductJson();
      this.attachClickHandler();
    }

    // Parse embedded JSON safely
    parseProductJson() {
      try {
        const jsonEl = this.querySelector('[data-json]');
        return jsonEl ? JSON.parse((jsonEl.textContent || '').trim() || 'null') : null;
      } catch (e) {
        console.warn('act-button: failed to parse data-json', e);
        return null;
      }
    }

    // Attach appropriate click handler based on product ID presence
    attachClickHandler() {
      if (this.productId) {
        this.addEventListener('click', () => this.handleClick());
      } else {
        this.addEventListener('click', () => this.openModalWithProduct());
      }
    }

    // Open modal and populate with product data
    openModalWithProduct() {
      console.log('Opening modal for product:', this.parsedProductJson);
      if (this.actModal && typeof this.actModal.renderModalContent === 'function') {
        this.actModal.renderModalContent(this.parsedProductJson || null);
      }
      if (this.actModal && typeof this.actModal.open === 'function') {
        this.actModal.open();
      }
    }

    handleClick() {
      this.addToCart([{ id: Number(this.productId), quantity: 1 }]);
    }

    async addToCart(items) {
      this.setAttribute('data-loading', '');
      try {
        const data = await this.postCartItems(items);
        await this.updateCartDrawer();
        await this.openCartDrawer();
        console.log(data);
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        this.removeAttribute('data-loading');
      }
    }

    // Post items to Shopify Cart API
    async postCartItems(items) {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      return response.json();
    }

    // Fetch and update cart drawer content
    async updateCartDrawer() {
      const newHTML = await this.fetchSection();
      const updatedCart = new DOMParser().parseFromString(newHTML, 'text/html').querySelector('.cart-drawer .drawer__inner');
      const currentContent = document.querySelector('.drawer__inner');
      if (currentContent && updatedCart && currentContent.innerHTML !== updatedCart.innerHTML) {
        currentContent.innerHTML = updatedCart.innerHTML;
        this.cartDrawer.classList.remove('is-empty');
      }
    }

    async fetchSection() {
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
      return response.text();
    }

    async openCartDrawer() {
      if (this.cartDrawer && typeof this.cartDrawer.open === 'function') {
        await this.cartDrawer.open();
      }
    }
  }
  customElements.define('act-button', ActButton);
}

if (!customElements.get('act-modal')) {
  class ActModal extends HTMLElement {
    constructor() {
      super();
      this.cacheElements();
      this.storeButtonText();
      this.attachEventListeners();
    }

    // Cache DOM elements for reuse
    cacheElements() {
      this.closeButton = this.querySelector('.act-modal__close');
      this.mediaEl = this.querySelector('[data-media]');
      this.titleEl = this.querySelector('[data-title]');
      this.pickerEl = this.querySelector('[data-picker]');
      this.selectedEl = this.querySelector('[data-selected]');
      this.dropdownEl = this.querySelector('[data-dropdown]');
      this.addButton = this.querySelector('[data-add]');
      this.background = this.querySelector('.act-modal__background');
    }

    // Store original button text for price updates
    storeButtonText() {
      if (this.addButton && this.addButton.textContent) {
        this.addButton.setAttribute('data-button-text', this.addButton.textContent.trim());
      }
    }

    // Attach all event listeners
    attachEventListeners() {
      this.closeButton && this.closeButton.addEventListener('click', () => this.close());
      this.background && this.background.addEventListener('click', () => this.close());
      this.selectedEl && this.selectedEl.addEventListener('click', () => this.toggleDropdown());
      this.addButton && this.addButton.addEventListener('click', () => this.handleAddToCart());
    }

    // Toggle dropdown open/closed state
    toggleDropdown() {
      const isOpen = this.dropdownEl && this.dropdownEl.hasAttribute('data-open');
      if (isOpen) {
        this.dropdownEl.removeAttribute('data-open');
        this.selectedEl.removeAttribute('data-open');
      } else {
        this.dropdownEl && this.dropdownEl.setAttribute('data-open', '');
        this.selectedEl.setAttribute('data-open', '');
      }
    }

    open() {
      this.setAttribute('data-show', '');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.removeAttribute('data-show');
      this.dropdownEl && this.dropdownEl.removeAttribute('data-open');
      this.selectedEl && this.selectedEl.removeAttribute('data-open');
      document.body.style.overflow = '';
    }

    async handleAddToCart() {
      const variantId = this.addButton.getAttribute('data-variant-id');
      if (!variantId) {
        console.warn('No variant selected');
        return;
      }

      const actButton = document.querySelector('act-button');
      if (actButton && typeof actButton.addToCart === 'function') {
        this.addButton.setAttribute('data-loading', '');
        await actButton.addToCart([{ id: Number(variantId), quantity: 1 }]);
        this.addButton.removeAttribute('data-loading');
        this.close();
      }
    }

    // Populate modal with product data
    renderModalContent(product) {
      if (!product) {
        this.clearContent();
        return;
      }

      this.setTitle(product.title);
      this.setMedia(product);
      this.populateVariants(product);
    }

    // Clear all modal content
    clearContent() {
      this.titleEl && (this.titleEl.textContent = '');
      this.mediaEl && (this.mediaEl.innerHTML = '');
      this.dropdownEl && (this.dropdownEl.innerHTML = '');
      this.selectedEl && (this.selectedEl.textContent = '');
      this.addButton && this.addButton.removeAttribute('data-variant-id');
    }

    // Set modal title
    setTitle(title) {
      if (this.titleEl) this.titleEl.textContent = title || '';
    }

    // Set modal media image
    setMedia(product) {
      let imgSrc = '';
      if (product.featured_image && product.featured_image.src) {
        imgSrc = product.featured_image.src;
      } else if (Array.isArray(product.images) && product.images.length) {
        imgSrc = product.images[0];
      }

      if (this.mediaEl) {
        this.mediaEl.innerHTML = imgSrc
          ? `<img src="${imgSrc}" alt="${(product.title || '').replace(/"/g, '&quot;')}" loading="lazy" class="act-modal__img">`
          : '';
      }
    }

    // Create and populate variant dropdown
    populateVariants(product) {
      if (!this.dropdownEl) return;

      this.dropdownEl.innerHTML = '';

      if (!Array.isArray(product.variants) || !product.variants.length) {
        this.selectedEl && (this.selectedEl.textContent = '');
        this.addButton && this.addButton.removeAttribute('data-variant-id');
        return;
      }

      product.variants.forEach((variant, idx) => {
        const label = this.createVariantLabel(variant, product);
        this.dropdownEl.appendChild(label);

        // Select first variant by default
        if (idx === 0) {
          label.querySelector('input').checked = true;
          this.selectVariant(variant, product);
        }
      });
    }

    // Create variant label element with radio input
    createVariantLabel(variant, product) {
      const label = document.createElement('label');
      label.className = 'act-modal__variant';

      // Hide variants containing "180" (temporary)
      if (variant.title && variant.title.includes('180')) {
        label.setAttribute('data-hidden', '');
      }

      const input = this.createVariantInput(variant);
      const textSpan = this.createVariantText(variant.title);
      const pricesDiv = this.createVariantPrices(variant);
      const markSpan = this.createVariantMark();

      label.appendChild(input);
      label.appendChild(textSpan);
      label.appendChild(pricesDiv);
      label.appendChild(markSpan);

      // Close dropdown if clicking already selected variant
      label.addEventListener('click', () => {
        if (input.checked) {
          this.closeDropdown();
        }
      });

      input.addEventListener('change', () => {
        if (input.checked) this.selectVariant(variant, product);
      });

      return label;
    }

    // Create variant radio input
    createVariantInput(variant) {
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'variant-radio';
      input.className = 'act-modal__input';
      input.dataset.input = '';
      input.dataset.variantId = variant.id;
      input.value = variant.id;
      return input;
    }

    // Create variant text span
    createVariantText(title) {
      const span = document.createElement('span');
      span.className = 'act-modal__text';
      span.textContent = title;
      return span;
    }

    // Create variant prices container
    createVariantPrices(variant) {
      const pricesDiv = document.createElement('div');
      pricesDiv.className = 'act-modal__prices';

      const price = variant.price ? (Number(variant.price) / 100).toFixed(2) : '';
      const compareAtPrice = variant.compare_at_price ? (Number(variant.compare_at_price) / 100).toFixed(2) : '';
      const currencySymbol = this.getCurrencySymbol();

      // Sale price display
      if (compareAtPrice && compareAtPrice !== price) {
        const compareAtSpan = document.createElement('span');
        compareAtSpan.className = 'act-modal__compare-at-price';
        compareAtSpan.textContent = `${currencySymbol}${compareAtPrice}`;
        pricesDiv.appendChild(compareAtSpan);

        const salePriceSpan = document.createElement('span');
        salePriceSpan.className = 'act-modal__sale-price';
        salePriceSpan.textContent = `${currencySymbol}${price}`;
        pricesDiv.appendChild(salePriceSpan);
      } else if (price) {
        const normalPriceSpan = document.createElement('span');
        normalPriceSpan.className = 'act-modal__normal-price';
        normalPriceSpan.textContent = `${currencySymbol}${price}`;
        pricesDiv.appendChild(normalPriceSpan);
      }

      return pricesDiv;
    }

    // Create variant checkmark
    createVariantMark() {
      const span = document.createElement('span');
      span.className = 'act-modal__mark';
      return span;
    }

    // Update UI when variant is selected
    selectVariant(variant, product) {
      this.updateSelectedText(variant.title);
      this.updateButtonPrice(variant);
      this.updateVariantImage(variant, product);
      this.closeDropdown();
    }

    // Update selected variant text
    updateSelectedText(title) {
      if (this.selectedEl) this.selectedEl.textContent = title || '';
    }

    // Update button with variant ID and price
    updateButtonPrice(variant) {
      if (!this.addButton) return;

      this.addButton.setAttribute('data-variant-id', variant.id);

      const price = variant.price ? (Number(variant.price) / 100).toFixed(2) : '';
      const currencySymbol = this.getCurrencySymbol();
      const buttonText = this.addButton.getAttribute('data-button-text') || 'Add to cart';
      const buttonContent = price ? `${buttonText} • ${currencySymbol}${price}` : buttonText;

      this.addButton.textContent = buttonContent;
    }

    // Update modal image if variant has featured image
    updateVariantImage(variant, product) {
      if (this.mediaEl && variant.featured_image) {
        const imgSrc = variant.featured_image.src || variant.featured_image;
        const altText = (variant.title || product.title || '').replace(/"/g, '&quot;');
        this.mediaEl.innerHTML = `<img src="${imgSrc}" alt="${altText}" loading="lazy" class="act-modal__img">`;
      }
    }

    // Close dropdown after selection
    closeDropdown() {
      this.dropdownEl && this.dropdownEl.removeAttribute('data-open');
      this.selectedEl && this.selectedEl.removeAttribute('data-open');
    }

    // Get currency symbol based on Shopify settings
    getCurrencySymbol() {
      const currencyCode = window.Shopify?.currency?.active || 'EUR';
      const symbols = {
        'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥',
        'CAD': 'CA$', 'AUD': 'A$', 'CHF': 'CHF', 'CNY': '¥',
        'SEK': 'kr', 'NZD': 'NZ$'
      };
      return symbols[currencyCode] || currencyCode;
    }
  }
  customElements.define('act-modal', ActModal);
}